import React, { useState, useEffect, useMemo } from 'react';
import {
  Package, AlertTriangle, TrendingDown, TrendingUp, Plus,
  Search, ChevronDown, ChevronUp, X, RefreshCw, Truck,
  BarChart2, Edit2, Check, Clock, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { postLog } from '../utils/authApi';
import { getCurrencyDisplay } from '../utils/currency';
const COUNTRY_CODES: Record<string, string> = {
  AF:'93',AL:'355',DZ:'213',AS:'1684',AD:'376',AO:'244',AI:'1264',AQ:'672',AG:'1268',AR:'54',
  AM:'374',AW:'297',AU:'61',AT:'43',AZ:'994',BS:'1242',BH:'973',BD:'880',BB:'1246',BY:'375',
  BE:'32',BZ:'501',BJ:'229',BM:'1441',BT:'975',BO:'591',BA:'387',BW:'267',BR:'55',IO:'246',
  VG:'1284',BN:'673',BG:'359',BF:'226',BI:'257',KH:'855',CM:'237',CA:'1',CV:'238',KY:'1345',
  CF:'236',TD:'235',CL:'56',CN:'86',CX:'61',CC:'61',CO:'57',KM:'269',CK:'682',CR:'506',
  HR:'385',CU:'53',CW:'599',CY:'357',CZ:'420',CD:'243',DK:'45',DJ:'253',DM:'1767',DO:'1809',
  EC:'593',EG:'20',SV:'503',GQ:'240',ER:'291',EE:'372',ET:'251',FK:'500',FO:'298',FJ:'679',
  FI:'358',FR:'33',GF:'594',PF:'689',GA:'241',GM:'220',GE:'995',DE:'49',GH:'233',GI:'350',
  GR:'30',GL:'299',GD:'1473',GP:'590',GU:'1671',GT:'502',GG:'44',GN:'224',GW:'245',GY:'592',
  HT:'509',HN:'504',HK:'852',HU:'36',IS:'354',IN:'91',ID:'62',IR:'98',IQ:'964',IE:'353',
  IM:'44',IL:'972',IT:'39',CI:'225',JM:'1876',JP:'81',JE:'44',JO:'962',KZ:'7',KE:'254',
  KI:'686',XK:'383',KW:'965',KG:'996',LA:'856',LV:'371',LB:'961',LS:'266',LR:'231',LY:'218',
  LI:'423',LT:'370',LU:'352',MO:'853',MK:'389',MG:'261',MW:'265',MY:'60',MV:'960',ML:'223',
  MT:'356',MH:'692',MQ:'596',MR:'222',MU:'230',YT:'262',MX:'52',FM:'691',MD:'373',MC:'377',
  MN:'976',ME:'382',MS:'1664',MA:'212',MZ:'258',MM:'95',NA:'264',NR:'674',NP:'977',NL:'31',
  NC:'687',NZ:'64',NI:'505',NE:'227',NG:'234',NU:'683',NF:'672',KP:'850',MP:'1670',
  NO:'47',OM:'968',PK:'92',PW:'680',PS:'970',PA:'507',PG:'675',PY:'595',PE:'51',PH:'63',
  PN:'64',PL:'48',PT:'351',PR:'1787',QA:'974',CG:'242',RE:'262',RO:'40',RU:'7',RW:'250',
  BL:'590',SH:'290',KN:'1869',LC:'1758',MF:'590',PM:'508',VC:'1784',WS:'685',SM:'378',
  ST:'239',SA:'966',SN:'221',RS:'381',SC:'248',SL:'232',SG:'65',SX:'1721',SK:'421',SI:'386',
  SB:'677',SO:'252',ZA:'27',KR:'82',SS:'211',ES:'34',LK:'94',SD:'249',SR:'597',SJ:'47',
  SZ:'268',SE:'46',CH:'41',SY:'963',TW:'886',TJ:'992',TZ:'255',TH:'66',TL:'670',TG:'228',
  TK:'690',TO:'676',TT:'1868',TN:'216',TR:'90',TM:'993',TC:'1649',TV:'688',VI:'1340',
  UG:'256',UA:'380',AE:'971',GB:'44',US:'1',UY:'598',UZ:'998',VU:'678',VA:'39',VE:'58',
  VN:'84',WF:'681',EH:'212',YE:'967',ZM:'260',ZW:'263',
};
const POPULAR_COUNTRIES = ['IN','AE','US','GB','SA','SG','AU','CA','PK','BD','MY','QA','KW','OM','BH'];

function CountryPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);
  const allCodes = Object.keys(COUNTRY_CODES);
  const filtered = search
    ? allCodes.filter(c => c.toLowerCase().includes(search.toLowerCase()) || COUNTRY_CODES[c]?.includes(search.replace('+','')))
    : [...POPULAR_COUNTRIES, ...allCodes.filter(c => !POPULAR_COUNTRIES.includes(c))];
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} style={{
        display:'flex', alignItems:'center', gap:4, background:'#f8fafc',
        border:'1.5px solid #e5e7eb', borderRadius:'8px 0 0 8px',
        padding:'8px 10px', cursor:'pointer', fontSize:12, fontWeight:700,
        color:'#111827', minWidth:72, whiteSpace:'nowrap',
      }}>
        +{COUNTRY_CODES[value]||'91'} <span style={{fontSize:9,opacity:0.5}}>▾</span>
      </button>
      {open && (
        <div onClick={e=>e.stopPropagation()} style={{
          position:'absolute', top:'100%', left:0, zIndex:9999,
          background:'#fff', border:'1px solid #e5e7eb', borderRadius:10,
          boxShadow:'0 20px 60px rgba(0,0,0,0.15)', width:200, maxHeight:260,
          overflow:'hidden', display:'flex', flexDirection:'column',
        }}>
          <div style={{padding:8}}>
            <input autoFocus placeholder="Search country or +code..." value={search}
              onChange={e=>setSearch(e.target.value)}
              style={{width:'100%',border:'1px solid #e5e7eb',borderRadius:6,padding:'5px 8px',fontSize:12,outline:'none',boxSizing:'border-box'}} />
          </div>
          <div style={{overflowY:'auto',flex:1}}>
            {filtered.map(c => (
              <button key={c} onClick={()=>{onChange(c);setOpen(false);setSearch('');}}
                style={{
                  display:'flex',alignItems:'center',gap:8,width:'100%',
                  background:c===value?'#f0f9ff':'transparent',
                  border:'none',padding:'7px 12px',cursor:'pointer',fontSize:12,color:'#374151',textAlign:'left',
                }}>
                <span style={{fontWeight:700,color:'#6366f1',minWidth:32}}>+{COUNTRY_CODES[c]}</span>
                <span>{c}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function detectCountryCode(adminDefault?: string | null): string {
  if (adminDefault && COUNTRY_CODES[adminDefault]) return adminDefault;

  const userDefault = localStorage.getItem('default_phone_country');
  if (userDefault && COUNTRY_CODES[userDefault]) return userDefault;

  const cached = sessionStorage.getItem('detected_country');
  if (cached && COUNTRY_CODES[cached]) return cached;

  try {
    const langRegion = navigator.language?.split('-')[1]?.toUpperCase();
    if (langRegion && COUNTRY_CODES[langRegion]) {
      sessionStorage.setItem('detected_country', langRegion);
      return langRegion;
    }
  } catch {
    // fall through to timezone
  }

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzToCountry: Record<string, string> = {
    'Asia/Kolkata':'IN','Asia/Calcutta':'IN',
    'Asia/Dubai':'AE','Asia/Muscat':'OM',
    'Asia/Riyadh':'SA','Asia/Kuwait':'KW','Asia/Qatar':'QA','Asia/Bahrain':'BH',
    'Asia/Karachi':'PK','Asia/Dhaka':'BD','Asia/Kathmandu':'NP','Asia/Colombo':'LK',
    'Asia/Singapore':'SG','Asia/Kuala_Lumpur':'MY','Asia/Jakarta':'ID','Asia/Manila':'PH',
    'Asia/Bangkok':'TH','Asia/Ho_Chi_Minh':'VN','Asia/Yangon':'MM','Asia/Phnom_Penh':'KH',
    'Asia/Tokyo':'JP','Asia/Seoul':'KR','Asia/Shanghai':'CN','Asia/Hong_Kong':'HK',
    'Asia/Taipei':'TW','Asia/Macau':'MO','Asia/Ulaanbaatar':'MN',
    'Asia/Kabul':'AF','Asia/Tehran':'IR','Asia/Baghdad':'IQ','Asia/Beirut':'LB',
    'Asia/Amman':'JO','Asia/Jerusalem':'IL','Asia/Damascus':'SY','Asia/Aden':'YE',
    'Asia/Tashkent':'UZ','Asia/Almaty':'KZ','Asia/Baku':'AZ','Asia/Tbilisi':'GE','Asia/Yerevan':'AM',
    'Europe/London':'GB','Europe/Dublin':'IE','Europe/Lisbon':'PT',
    'Europe/Paris':'FR','Europe/Berlin':'DE','Europe/Amsterdam':'NL','Europe/Brussels':'BE',
    'Europe/Madrid':'ES','Europe/Rome':'IT','Europe/Vienna':'AT','Europe/Zurich':'CH',
    'Europe/Stockholm':'SE','Europe/Oslo':'NO','Europe/Copenhagen':'DK','Europe/Helsinki':'FI',
    'Europe/Warsaw':'PL','Europe/Prague':'CZ','Europe/Budapest':'HU','Europe/Bucharest':'RO',
    'Europe/Sofia':'BG','Europe/Athens':'GR','Europe/Istanbul':'TR',
    'Europe/Moscow':'RU','Europe/Kiev':'UA','Europe/Minsk':'BY',
    'America/New_York':'US','America/Chicago':'US','America/Denver':'US',
    'America/Los_Angeles':'US','America/Anchorage':'US','Pacific/Honolulu':'US',
    'America/Toronto':'CA','America/Vancouver':'CA','America/Montreal':'CA',
    'America/Mexico_City':'MX','America/Sao_Paulo':'BR','America/Buenos_Aires':'AR',
    'America/Bogota':'CO','America/Lima':'PE','America/Santiago':'CL',
    'America/Caracas':'VE','America/La_Paz':'BO',
    'Australia/Sydney':'AU','Australia/Melbourne':'AU','Australia/Brisbane':'AU',
    'Australia/Perth':'AU','Pacific/Auckland':'NZ',
    'Africa/Cairo':'EG','Africa/Lagos':'NG','Africa/Nairobi':'KE',
    'Africa/Johannesburg':'ZA','Africa/Accra':'GH','Africa/Casablanca':'MA',
    'Africa/Addis_Ababa':'ET','Africa/Dar_es_Salaam':'TZ','Africa/Kampala':'UG',
  };
  const code = tzToCountry[tz] || 'IN';
  sessionStorage.setItem('detected_country', code);
  return code;
}

function parsePhoneValue(value: string, defaultCountry = 'IN'): { country: string; dialCode: string; nationalNumber: string } {
  const fallbackCountry = defaultCountry && COUNTRY_CODES[defaultCountry] ? defaultCountry : 'IN';
  const fallbackDialCode = (COUNTRY_CODES[fallbackCountry] || '91').replace(/\D/g, '');

  if (!value) return { country: fallbackCountry, dialCode: fallbackDialCode, nationalNumber: '' };

  const raw = String(value).trim();
  const hasLeadingPlus = raw.startsWith('+');
  let cleanDigits = raw.replace(/\D/g, '');

  if (hasLeadingPlus) {
    let bestCountry = fallbackCountry;
    let bestDialCode = fallbackDialCode;
    let maxCodeLen = 0;

    const sortedEntries = Object.entries(COUNTRY_CODES).sort(
      (a, b) => b[1].replace(/\D/g, '').length - a[1].replace(/\D/g, '').length
    );

    for (const [c, code] of sortedEntries) {
      const pureCode = code.replace(/\D/g, '');
      if (cleanDigits.startsWith(pureCode) && pureCode.length > maxCodeLen) {
        bestCountry = c;
        bestDialCode = pureCode;
        maxCodeLen = pureCode.length;
      }
    }

    return {
      country: bestCountry,
      dialCode: bestDialCode,
      nationalNumber: cleanDigits.slice(bestDialCode.length),
    };
  }

  if (cleanDigits.startsWith('0') && fallbackCountry !== 'IT' && cleanDigits.length >= 7) {
    cleanDigits = cleanDigits.replace(/^0/, '');
  }

  if (cleanDigits.startsWith(fallbackDialCode) && cleanDigits.length >= fallbackDialCode.length + 5) {
    return {
      country: fallbackCountry,
      dialCode: fallbackDialCode,
      nationalNumber: cleanDigits.slice(fallbackDialCode.length),
    };
  }

  return {
    country: fallbackCountry,
    dialCode: fallbackDialCode,
    nationalNumber: cleanDigits,
  };
}

export function getCleanE164Phone(phone: string, defaultCountry = 'IN'): string {
  if (!phone) return '';
  const parsed = parsePhoneValue(phone, defaultCountry);
  const national = parsed.nationalNumber.replace(/\D/g, '');
  const dial = parsed.dialCode.replace(/\D/g, '');
  const total = `${dial}${national}`;

  if (national.length < 4 || total.length < 7 || total.length > 15) {
    return '';
  }
  return `+${dial}${national}`;
}

function PhoneInput({ value, onChange, orgDefault, outletDefault }: { value: string; onChange: (v: string) => void; orgDefault?: string | null; outletDefault?: string | null }) {
  const defaultCountry = detectCountryCode(outletDefault || orgDefault);
  const parsed = parsePhoneValue(value, defaultCountry);

  const [country, setCountry] = React.useState(() => {
    if (value && value.startsWith('+')) return parsed.country;
    return defaultCountry;
  });
  const [number, setNumber] = React.useState(() => parsed.nationalNumber);
  const lastEmitted = React.useRef(value);

  React.useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    if (!value) {
      setNumber('');
    } else {
      const p = parsePhoneValue(value, defaultCountry);
      setCountry(p.country);
      setNumber(p.nationalNumber);
    }
  }, [value, defaultCountry]);

  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    const dialCode = COUNTRY_CODES[newCountry] || '91';
    const cleanDigits = number.trim();
    // Do not emit "+91" if user hasn't typed any digits
    const formatted = cleanDigits ? `+${dialCode}${cleanDigits}` : '';
    lastEmitted.current = formatted;
    onChange(formatted);
  };

  const handleNumberChange = (raw: string) => {
    let cleanNum = raw.replace(/\D/g, '');
    const dialCode = (COUNTRY_CODES[country] || '91').replace(/\D/g, '');

    // Strip dial code if pasted with '+' or duplicate country prefix
    if (raw.includes('+') && cleanNum.startsWith(dialCode)) {
      cleanNum = cleanNum.slice(dialCode.length);
    } else if (cleanNum.startsWith(dialCode) && cleanNum.length > 10) {
      cleanNum = cleanNum.slice(dialCode.length);
    }

    setNumber(cleanNum);
    const formatted = cleanNum ? `+${dialCode}${cleanNum}` : '';
    lastEmitted.current = formatted;
    onChange(formatted);
  };

  return (
    <div style={{ display: 'flex' }}>
      <CountryPicker value={country} onChange={handleCountryChange} />
      <input
        type="tel"
        value={number}
        onChange={e => handleNumberChange(e.target.value)}
        placeholder="Phone number"
        style={{
          flex: 1, border: '1.5px solid #e5e7eb', borderLeft: 'none',
          borderRadius: '0 8px 8px 0', padding: '8px 12px',
          fontSize: 12, outline: 'none', background: '#fff',
          fontWeight: 600, color: '#111827', boxSizing: 'border-box',
        }}
      />
    </div>
  );
}


const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

function getToken() {
  return localStorage.getItem('cloudairambo_jwt') || '';
}

function authHeaders() {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` };
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface InventoryItem {
  id: number;
  name: string;
  barcode: string;
  category?: string;
  unit?: string;
  stock_quantity: number;
  low_stock_threshold: number;
  is_low: boolean;
}

interface StockMovement {
  id: number;
  movement_type: string;
  quantity: number;
  reference_id?: number;
  note?: string;
  created_at: string;
}

interface Supplier {
  id: number;
  name: string;
  phone?: string;
  email?: string;
  address?: string;
}

interface PurchaseOrder {
  id: number;
  supplier_id?: number;
  status: string;
  total_cost: number;
  note?: string;
  created_at: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function movementColor(type: string) {
  switch (type) {
    case 'sale': return '#ef4444';
    case 'purchase': return '#10b981';
    case 'return': return '#3b82f6';
    case 'damage': return '#f59e0b';
    default: return '#6b7280';
  }
}

function movementIcon(type: string) {
  switch (type) {
    case 'sale': return <ArrowDownRight size={12} />;
    case 'purchase': return <ArrowUpRight size={12} />;
    case 'return': return <ArrowUpRight size={12} />;
    case 'damage': return <ArrowDownRight size={12} />;
    default: return <BarChart2 size={12} />;
  }
}

function fmt(n: number) {
  return n > 0 ? `+${n}` : String(n);
}

// ─── Modals ───────────────────────────────────────────────────────────────────

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
      backdropFilter: 'blur(3px)', zIndex: 300,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: 24, width: '100%', maxWidth: 460,
        boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <span style={{ fontWeight: 800, fontSize: 15, color: '#111827' }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Adjust Modal ─────────────────────────────────────────────────────────────

function AdjustModal({ item, outletId, onClose, onDone }: { item: InventoryItem; outletId?: number | null; onClose: () => void; onDone: () => void }) {
  const [qty, setQty] = useState('');
  const [type, setType] = useState<'adjustment' | 'damage' | 'return'>('adjustment');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    let q = parseFloat(qty);
    if (!q || q === 0) return alert('Enter a non-zero quantity.');
    if (type === 'damage' || type === 'return') {
      q = -Math.abs(q);
    }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND}/inventory/adjust`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ product_id: item.id, quantity: q, movement_type: type, note, outlet_id: outletId || null }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed');
      }
      postLog('INVENTORY_ADJUSTED', `Stock adjusted: ${item.name} | Type: ${type} | Qty: ${q > 0 ? '+' : ''}${q}${note ? ` | Note: ${note}` : ''}`);
      onDone();
      onClose();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const inp: React.CSSProperties = {
    width: '100%', padding: '8px 11px', borderRadius: 8,
    border: '1.5px solid #e5e7eb', fontSize: 13, outline: 'none',
    boxSizing: 'border-box', color: '#111827',
  };
  const lbl: React.CSSProperties = {
    fontSize: 11, fontWeight: 800, color: '#1e293b',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    display: 'block', marginBottom: 5,
  };

  return (
    <Modal title={`Adjust Stock — ${item.name}`} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ background: '#f8fafc', borderRadius: 10, padding: '10px 14px', display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 12, color: '#1e293b', fontWeight: 700 }}>Current stock</span>
          <span style={{ fontSize: 14, fontWeight: 800, color: item.is_low ? '#ef4444' : '#0f172a' }}>
            {item.stock_quantity} {item.unit || 'units'}
          </span>
        </div>

        <div>
          <label style={lbl}>Adjustment Type</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {(['adjustment', 'damage', 'return'] as const).map(t => (
              <button key={t} onClick={() => setType(t)} style={{
                flex: 1, padding: '6px 4px', borderRadius: 8, border: '1.5px solid',
                fontSize: 11, fontWeight: 700, cursor: 'pointer',
                background: type === t ? (t === 'damage' ? '#fef2f2' : t === 'return' ? '#eff6ff' : '#f0fdf4') : '#fff',
                color: type === t ? (t === 'damage' ? '#ef4444' : t === 'return' ? '#3b82f6' : '#10b981') : '#6b7280',
                borderColor: type === t ? (t === 'damage' ? '#fca5a5' : t === 'return' ? '#93c5fd' : '#86efac') : '#e5e7eb',
              }}>
                {t === 'adjustment' ? '⚖ Adjust' : t === 'damage' ? '⚠ Damage' : '↩ Return'}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label style={lbl}>Quantity (+ to add, − to remove)</label>
          <input
            type="number"
            style={inp}
            placeholder="e.g. +50 or -5"
            value={qty}
            onChange={e => setQty(e.target.value)}
          />
        </div>

        <div>
          <label style={lbl}>Note (optional)</label>
          <input style={inp} placeholder="Reason for adjustment..." value={note} onChange={e => setNote(e.target.value)} />
        </div>

        <button onClick={handleSubmit} disabled={saving} style={{
          width: '100%', padding: 11, borderRadius: 10, border: 'none',
          background: 'linear-gradient(to right,#3b82f6,#6366f1)',
          color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer',
          opacity: saving ? 0.7 : 1,
        }}>
          {saving ? 'Saving...' : 'Save Adjustment'}
        </button>
      </div>
    </Modal>
  );
}

// ─── Ledger Drawer ────────────────────────────────────────────────────────────

function LedgerDrawer({ item, outletId, onClose }: { item: InventoryItem; outletId?: number | null; onClose: () => void }) {
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = outletId ? `?outlet_id=${outletId}` : '';
    fetch(`${BACKEND}/inventory/movements/${item.id}${q}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(data => { setMovements(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [item.id, outletId]);

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)',
      backdropFilter: 'blur(2px)', zIndex: 300,
      display: 'flex', justifyContent: 'flex-end',
    }} onClick={onClose}>
      <div style={{
        width: 380, height: '100%', background: '#fff',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.12)',
        display: 'flex', flexDirection: 'column',
        animation: 'slideIn 0.2s ease',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: '18px 20px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>{item.name}</div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>Stock Ledger</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
              <X size={16} />
            </button>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <div style={{ flex: 1, background: '#f8fafc', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Current</div>
              <div style={{ fontSize: 20, fontWeight: 900, color: item.is_low ? '#ef4444' : '#10b981' }}>
                {item.stock_quantity}
              </div>
            </div>
            <div style={{ flex: 1, background: '#f8fafc', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Threshold</div>
              <div style={{ fontSize: 20, fontWeight: 900, color: '#f59e0b' }}>{item.low_stock_threshold}</div>
            </div>
            <div style={{ flex: 1, background: '#f8fafc', borderRadius: 8, padding: '8px 12px', textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Movements</div>
              <div style={{ fontSize: 20, fontWeight: 900, color: '#6366f1' }}>{movements.length}</div>
            </div>
          </div>
        </div>

        {/* Movements list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', paddingTop: 40, fontSize: 13 }}>Loading...</div>
          ) : movements.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', paddingTop: 40 }}>
              <Clock size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
              <div style={{ fontSize: 13, fontWeight: 600 }}>No movements yet</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {movements.map(m => (
                <div key={m.id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 12px', borderRadius: 10,
                  background: '#fafafa', border: '1px solid #f1f5f9',
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                    background: movementColor(m.movement_type) + '18',
                    color: movementColor(m.movement_type),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {movementIcon(m.movement_type)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', textTransform: 'capitalize' }}>
                      {m.movement_type}
                    </div>
                    {m.note && <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.note}</div>}
                    <div style={{ fontSize: 10, color: '#d1d5db', marginTop: 1 }}>
                      {new Date(m.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div style={{
                    fontSize: 13, fontWeight: 800,
                    color: m.quantity >= 0 ? '#10b981' : '#ef4444',
                    flexShrink: 0,
                  }}>
                    {fmt(m.quantity)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Purchase Order Modal ────────────────────────────────────────────────────

function PurchaseOrderModal({ suppliers, inventory, outletId, onClose, onDone }: {
  suppliers: Supplier[];
  inventory: InventoryItem[];
  outletId?: number | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [supplierId, setSupplierId] = useState('');
  const [note, setNote] = useState('');
  const [items, setItems] = useState([{ product_id: '', quantity: '', unit_cost: '' }]);
  const [saving, setSaving] = useState(false);
  const currSym = getCurrencyDisplay(
    localStorage.getItem('cloudairambo_active_currency') || 'INR',
    (localStorage.getItem('cloudairambo_currency_display_mode') as 'symbol' | 'code') || 'symbol'
  );
  const addRow = () => setItems(prev => [...prev, { product_id: '', quantity: '', unit_cost: '' }]);
  const removeRow = (i: number) => setItems(prev => prev.filter((_, j) => j !== i));
  const updateRow = (i: number, field: string, val: string) =>
    setItems(prev => prev.map((row, j) => j === i ? { ...row, [field]: val } : row));

  const total = items.reduce((s, r) => s + (parseFloat(r.quantity) || 0) * (parseFloat(r.unit_cost) || 0), 0);

  const handleSubmit = async () => {
    const validItems = items.filter(r => r.product_id && r.quantity);
    if (!validItems.length) return alert('Add at least one item.');
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND}/inventory/purchase-orders`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          supplier_id: supplierId ? parseInt(supplierId) : null,
          note,
          outlet_id: outletId || null,
          items: validItems.map(r => ({
            product_id: parseInt(r.product_id),
            quantity: parseFloat(r.quantity),
            unit_cost: parseFloat(r.unit_cost) || 0,
          })),
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      const saved = await res.json();
      const supplierName = suppliers.find(s => s.id === parseInt(supplierId))?.name;
      postLog('INVENTORY_PO_CREATED', `Purchase Order #${saved.id} created${supplierName ? ` | Supplier: ${supplierName}` : ''}${total > 0 ? ` | ${currSym}${total.toFixed(2)}` : ''} | ${validItems.length} item(s)${note ? ` | ${note}` : ''}`);
      onDone();
      onClose();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  const inp: React.CSSProperties = {
    width: '100%', padding: '7px 10px', borderRadius: 7,
    border: '1.5px solid #e5e7eb', fontSize: 12, outline: 'none',
    boxSizing: 'border-box', color: '#111827',
  };
  const lbl: React.CSSProperties = {
    fontSize: 11, fontWeight: 800, color: '#1e293b',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    display: 'block', marginBottom: 5,
  };

  return (
    <Modal title="New Purchase Order" onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={lbl}>Supplier (optional)</label>
          <select value={supplierId} onChange={e => setSupplierId(e.target.value)} style={{ ...inp }}>
            <option value="">— No supplier —</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <label style={{ ...lbl, marginBottom: 0 }}>Items</label>
            <button onClick={addRow} style={{ fontSize: 11, color: '#6366f1', background: '#ede9fe', border: 'none', borderRadius: 6, padding: '3px 9px', cursor: 'pointer', fontWeight: 700 }}>
              + Row
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 20px', gap: 5 }}>
              {['Product', 'Qty', 'Cost', ''].map(h => (
                <div key={h} style={{ fontSize: 11, fontWeight: 800, color: '#334155', textTransform: 'uppercase' }}>{h}</div>
              ))}
            </div>
            {items.map((row, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 20px', gap: 5 }}>
                <select value={row.product_id} onChange={e => updateRow(i, 'product_id', e.target.value)} style={{ ...inp, fontSize: 11 }}>
                  <option value="">Select...</option>
                  {inventory.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <input type="number" placeholder="0" style={{ ...inp }} value={row.quantity} onChange={e => updateRow(i, 'quantity', e.target.value)} />
                <input type="number" placeholder="0.00" style={{ ...inp }} value={row.unit_cost} onChange={e => updateRow(i, 'unit_cost', e.target.value)} />
                <button onClick={() => removeRow(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', paddingTop: 4 }}>
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {total > 0 && (
          <div style={{ background: '#f0fdf4', borderRadius: 8, padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>Total Cost</span>
            <span style={{ fontSize: 15, fontWeight: 800, color: '#10b981' }}>{currSym}{total.toFixed(2)}</span>
          </div>
        )}

        <div>
          <label style={lbl}>Note</label>
          <input style={inp} placeholder="Optional note..." value={note} onChange={e => setNote(e.target.value)} />
        </div>

        <button onClick={handleSubmit} disabled={saving} style={{
          width: '100%', padding: 11, borderRadius: 10, border: 'none',
          background: 'linear-gradient(to right,#10b981,#059669)',
          color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer',
          opacity: saving ? 0.7 : 1,
        }}>
          {saving ? 'Creating...' : 'Create Purchase Order'}
        </button>
      </div>
    </Modal>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

import { AuthUser } from '../utils/authApi';
import InventoryReportsTab from './InventoryReportsTab';
import { getPermissions } from '../utils/permissions';

export default function InventoryView({ authUser, outletId = null, moduleStatuses = null }: { authUser: AuthUser | null; outletId?: number | null; moduleStatuses?: any }) {
 const perms = getPermissions(authUser?.role, authUser?.permissions, (authUser as any)?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses);
if (!perms.canAccessInventory) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '80px', 
        color: '#9ca3af', 
        fontSize: 14,
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}>
        You don't have permission to access Inventory.
      </div>
    );
  }
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      *::-webkit-scrollbar { width: 0px; height: 0px; display: none; }
      * { scrollbar-width: none; -ms-overflow-style: none; }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [outletPhoneDefault, setOutletPhoneDefault] = useState<string | null>(null);

  useEffect(() => {
    if (!outletId) { setOutletPhoneDefault(null); return; }
    fetch(`${BACKEND}/outlets`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => {
        const match = Array.isArray(d) ? d.find((o: any) => o.id === outletId) : null;
        setOutletPhoneDefault(match?.default_phone_country || null);
      })
      .catch(() => setOutletPhoneDefault(null));
  }, [outletId]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'low' | 'ok'>('all');
  const [tab, setTab] = useState<'stock' | 'orders' | 'suppliers' | 'reports'>('stock');
  const [reportRefreshKey, setReportRefreshKey] = useState(0);
  const currSym = getCurrencyDisplay(
    localStorage.getItem('cloudairambo_active_currency') || 'INR',
    (localStorage.getItem('cloudairambo_currency_display_mode') as 'symbol' | 'code') || 'symbol'
  );
  const [adjustItem, setAdjustItem] = useState<InventoryItem | null>(null);
  const [ledgerItem, setLedgerItem] = useState<InventoryItem | null>(null);
  const [showPOModal, setShowPOModal] = useState(false);
  const [showSupplierForm, setShowSupplierForm] = useState(false);

  // Supplier form
  const [supName, setSupName] = useState('');
  const [supPhone, setSupPhone] = useState('');
  const [supEmail, setSupEmail] = useState('');
  const [supAddress, setSupAddress] = useState('');
  const [supSaving, setSupSaving] = useState(false);

  // Inline threshold editing
  const [editingThreshold, setEditingThreshold] = useState<number | null>(null);
  const [thresholdVal, setThresholdVal] = useState('');
const exportPOExcel = () => {
  const ws = XLSX.utils.json_to_sheet(purchaseOrders.map(po => ({
    'PO ID': po.id,
    Supplier: suppliers.find(s => s.id === po.supplier_id)?.name || '',
    Status: po.status,
    'Total Cost': po.total_cost,
    Note: po.note || '',
    Date: new Date(po.created_at).toLocaleDateString(),
  })));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Purchase Orders');
  XLSX.writeFile(wb, 'purchase_orders.xlsx');
};
  const fetchAll = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const invQs = outletId ? `?outlet_id=${outletId}` : '';
      const [inv, sup, pos] = await Promise.all([
        fetch(`${BACKEND}/inventory/${invQs}`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${BACKEND}/inventory/suppliers${invQs}`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${BACKEND}/inventory/purchase-orders${invQs}`, { headers: authHeaders() }).then(r => r.json()),
      ]);
      setInventory(Array.isArray(inv) ? inv : []);
      setSuppliers(Array.isArray(sup) ? sup : []);
      setPurchaseOrders(Array.isArray(pos) ? pos : []);
    } catch (e) {
      console.error(e);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [outletId]);

  // Silent background sync — keeps web in sync with app/other tabs
  useEffect(() => {
    const interval = setInterval(() => fetchAll(true), 15000);
    const onVisible = () => { if (document.visibilityState === 'visible') fetchAll(true); };
    const onFocus = () => fetchAll(true);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [outletId]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return inventory.filter(item => {
      const matchSearch = !q || item.name.toLowerCase().includes(q) || item.barcode?.toLowerCase().includes(q) || (item.category || '').toLowerCase().includes(q);
      const matchFilter = filter === 'all' || (filter === 'low' && item.is_low) || (filter === 'ok' && !item.is_low);
      return matchSearch && matchFilter;
    });
  }, [inventory, search, filter]);

  const lowCount = inventory.filter(i => i.is_low).length;

  const receiveOrder = async (poId: number) => {
    try {
      const res = await fetch(`${BACKEND}/inventory/purchase-orders/${poId}/receive`, {
        method: 'PATCH', headers: authHeaders(),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed');
      const po = purchaseOrders.find(p => p.id === poId);
      const supplier = suppliers.find(s => s.id === po?.supplier_id);
      postLog('INVENTORY_PO_RECEIVED', `PO #${poId} received${supplier ? ` | Supplier: ${supplier.name}` : ''}${po?.total_cost ? ` | ${currSym}${po.total_cost.toFixed(2)}` : ''}`);
      fetchAll();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const saveThreshold = async (item: InventoryItem) => {
    const val = parseInt(thresholdVal);
    if (isNaN(val) || val < 0) return;
    try {
      await fetch(`${BACKEND}/products/${item.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ low_stock_threshold: val }),
      });
      postLog('INVENTORY_THRESHOLD_UPDATED', `Low stock threshold updated: ${item.name} | ${item.low_stock_threshold} → ${val}`);
      setEditingThreshold(null);
      fetchAll();
    } catch (e) {
      alert('Failed to update threshold');
    }
  };

  const createSupplier = async () => {
    if (!supName.trim()) return alert('Name required');

    const defaultCountry = detectCountryCode(outletPhoneDefault || (authUser as any)?.default_phone_country);
    const cleanPhone = supPhone.trim() ? getCleanE164Phone(supPhone, defaultCountry) : '';

    if (supPhone.trim() && !cleanPhone) {
      alert('Please enter a valid phone number (between 7 and 15 digits).');
      return;
    }

    setSupSaving(true);
    try {
      const res = await fetch(`${BACKEND}/inventory/suppliers`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          name: supName.trim(),
          phone: cleanPhone || null,
          email: supEmail.trim() || null,
          address: supAddress.trim() || null,
          outlet_id: outletId || null,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create supplier');
      }

      postLog('INVENTORY_SUPPLIER_ADDED', `Supplier added: ${supName}${cleanPhone ? ` | ${cleanPhone}` : ''}${supEmail ? ` | ${supEmail}` : ''}${supAddress ? ` | ${supAddress}` : ''}`);
      setSupName(''); setSupPhone(''); setSupEmail(''); setSupAddress('');
      setShowSupplierForm(false);
      fetchAll();
    } catch (e: any) {
      alert(e.message || 'Failed to create supplier');
    } finally {
      setSupSaving(false);
    }
  };

  const deleteSupplier = async (id: number) => {
    if (!confirm('Delete this supplier?')) return;
    const supplier = suppliers.find(s => s.id === id);
    await fetch(`${BACKEND}/inventory/suppliers/${id}`, { method: 'DELETE', headers: authHeaders() });
    postLog('INVENTORY_SUPPLIER_DELETED', `Supplier deleted: ${supplier?.name || `#${id}`}`);
    fetchAll();
  };

  // ── Styles ─────────────────────────────────────────────────────────────────

  const inp: React.CSSProperties = {
    padding: '7px 10px', borderRadius: 8, border: '1.5px solid #e5e7eb',
    fontSize: 12, outline: 'none', color: '#111827', boxSizing: 'border-box' as const,
  };


// ── Excel Import: Suppliers ──────────────────────────────────────────
const importSuppliersExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0];
  if (!file) return;
  const wb = XLSX.read(await file.arrayBuffer());
  const rows: any[] = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
  let added = 0;
  const defaultCountry = detectCountryCode(outletPhoneDefault || (authUser as any)?.default_phone_country);

  for (const row of rows) {
    const name = row['Name'] || row['name'];
    if (!name) continue;
    const rawPhone = String(row['Phone'] || row['phone'] || '').trim();
    const cleanPhone = rawPhone ? getCleanE164Phone(rawPhone, defaultCountry) || rawPhone : '';

    await fetch(`${BACKEND}/inventory/suppliers`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        name,
        phone: cleanPhone || null,
        email: row['Email'] || row['email'] || '',
        address: row['Address'] || row['address'] || '',
        outlet_id: outletId || null,
      }),
    });
    added++;
  }
  postLog('INVENTORY_SUPPLIERS_IMPORTED', `${added} suppliers imported from Excel`);
    alert(`Imported ${added} suppliers`);
    fetchAll();
  e.target.value = '';
};

// ── Excel Import: Purchase Order ─────────────────────────────────────
const importPOExcel = async (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0];
  if (!file) return;
  const wb = XLSX.read(await file.arrayBuffer());
  const rows: any[] = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
  const poItems: any[] = [];
  for (const row of rows) {
    const barcode = row['Barcode'] || row['barcode'];
    const name    = row['Name']    || row['name'];
    const qty     = Number(row['Quantity'] || row['quantity'] || 0);
    const cost    = Number(row['Unit Cost'] || row['unit_cost'] || row['Cost'] || 0);
    const product = inventory.find(
      (p: any) =>
        (barcode && p.barcode?.toLowerCase() === String(barcode).toLowerCase()) ||
        (name && p.name.toLowerCase() === String(name).toLowerCase())
    );
    if (!product || qty <= 0) continue;
    poItems.push({ product_id: product.id, quantity: qty, unit_cost: cost });
  }
  if (poItems.length === 0) { alert('No matching products found. Check Barcode/Name columns.'); return; }
  const res = await fetch(`${BACKEND}/inventory/purchase-orders`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ supplier_id: null, items: poItems, note: 'Imported from Excel', outlet_id: outletId || null }),
  });
  if (res.ok) {
  const po = await res.json();
  const autoReceive = confirm('Auto-receive this order? Click OK to update stock immediately, Cancel to keep as pending.');
  if (autoReceive) {
    await fetch(`${BACKEND}/inventory/purchase-orders/${po.id}/receive`, {
      method: 'PATCH', headers: authHeaders(),
    });
    postLog('INVENTORY_PO_IMPORTED', `PO #${po.id} imported and received from Excel | ${poItems.length} item(s)`);
    alert(`Imported and received ${poItems.length} items — stock updated`);
  } else {
    postLog('INVENTORY_PO_IMPORTED', `PO #${po.id} imported from Excel (pending) | ${poItems.length} item(s)`);
    alert(`PO created with ${poItems.length} items — mark received manually`);
  }
  fetchAll();
}
  else { const err = await res.json(); alert('Error: ' + JSON.stringify(err)); }
  e.target.value = '';
};

// ── Excel Export: Suppliers ──────────────────────────────────────────
const exportSuppliersExcel = () => {
  const ws = XLSX.utils.json_to_sheet(suppliers.map(s => ({
    Name: s.name,
    Phone: s.phone || '',
    Email: s.email || '',
    Address: s.address || '',
  })));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Suppliers');
  XLSX.writeFile(wb, 'suppliers.xlsx');
};

// ── Excel Export: Inventory ──────────────────────────────────────────
const exportInventoryExcel = () => {
  const ws = XLSX.utils.json_to_sheet(inventory.map(i => ({
    Name: i.name,
    Barcode: i.barcode,
    Category: i.category || '',
    Unit: i.unit || '',
    Stock: i.stock_quantity,
    Threshold: i.low_stock_threshold,
    Status: i.is_low ? 'Low' : 'OK',
  })));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Inventory');
  XLSX.writeFile(wb, 'inventory.xlsx');
};

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column', 
      background: '#f0f4f8', 
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', 
      overflow: 'hidden' 
    }}>

      {/* ── Header ── */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '14px 20px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Package size={18} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight: 900, fontSize: 17, color: '#0f172a' }}>Inventory</div>
              <div style={{ fontSize: 12, color: '#334155', fontWeight: 600 }}>{inventory.length} products · {lowCount > 0 ? <span style={{ color: '#dc2626', fontWeight: 800 }}>{lowCount} low stock</span> : 'all stocked'}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
           <button onClick={() => { fetchAll(); setReportRefreshKey(k => k + 1); }} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: '1.5px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontSize: 12, color: '#0f172a', fontWeight: 700 }}>
  <RefreshCw size={13} /> Refresh
</button>
            {perms.canAddProduct && (
  <button onClick={() => setShowPOModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, border: 'none', background: 'linear-gradient(to right,#10b981,#059669)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700, boxShadow: '0 2px 8px rgba(16,185,129,0.3)' }}>
    <Truck size={13} /> New Purchase Order
  </button>
)}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginTop: 14 }}>
          {(['stock', 'orders', 'suppliers', 'reports'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '7px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontSize: 12, fontWeight: tab === t ? 800 : 600,
              background: tab === t ? '#ede9fe' : 'transparent',
              color: tab === t ? '#5b21b6' : '#334155',
              transition: 'all 0.15s',
            }}>
              {t === 'stock' ? `📦 Stock${lowCount > 0 ? ` (${lowCount} ⚠)` : ''}` : t === 'orders' ? `🚚 Purchase Orders (${purchaseOrders.length})` : t === 'suppliers' ? `🏭 Suppliers (${suppliers.length})` : `📊 Reports`}
            </button>
          ))}
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>

        {/* ── STOCK TAB ── */}
        {tab === 'stock' && (
          <>
            {/* Search + filter */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 180px', position: 'relative', minWidth: 160 }}>
                <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
                <input style={{ ...inp, paddingLeft: 30, width: '100%' }} placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                {(['all', 'low', 'ok'] as const).map(f => (
                  <button key={f} onClick={() => setFilter(f)} style={{
                    padding: '6px 12px', borderRadius: 8, border: '1.5px solid',
                    fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    background: filter === f ? (f === 'low' ? '#fef2f2' : f === 'ok' ? '#f0fdf4' : '#ede9fe') : '#fff',
                    color: filter === f ? (f === 'low' ? '#ef4444' : f === 'ok' ? '#10b981' : '#6366f1') : '#6b7280',
                    borderColor: filter === f ? (f === 'low' ? '#fca5a5' : f === 'ok' ? '#86efac' : '#c4b5fd') : '#e5e7eb',
                  }}>
                    {f === 'all' ? 'All' : f === 'low' ? '⚠ Low' : '✓ OK'}
                  </button>
                ))}
              </div>
              {perms.canExportExcel && (
              <button onClick={exportInventoryExcel} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 700, color: '#374151' }}>
                📤 Export Excel
              </button>
               )}
            </div>

            {/* Low stock alert banner */}
            {lowCount > 0 && filter === 'all' && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
                <AlertTriangle size={15} color="#ef4444" />
                <span style={{ fontSize: 12, color: '#dc2626', fontWeight: 700 }}>
                  {lowCount} product{lowCount > 1 ? 's are' : ' is'} below the low stock threshold
                </span>
                <button onClick={() => setFilter('low')} style={{ marginLeft: 'auto', fontSize: 11, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, textDecoration: 'underline' }}>
                  View all
                </button>
              </div>
            )}

            {/* Stock table */}
            {loading ? (
              <div style={{ textAlign: 'center', color: '#9ca3af', paddingTop: 60, fontSize: 13 }}>Loading inventory...</div>
            ) : filtered.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', textAlign: 'center', width: '100%' }}>
                <Package size={42} style={{ color: '#475569', marginBottom: 10 }} />
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>No products found</div>
              </div>
            ) : (
              <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                {/* Table header */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: 0, background: '#f1f5f9', borderBottom: '1.5px solid #cbd5e1', padding: '10px 16px', minWidth: 560 }}>
                  {['Product', 'Category', 'Stock', 'Threshold', 'Actions'].map(h => (
                    <div key={h} style={{ fontSize: 11, fontWeight: 800, color: '#1e293b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</div>
                  ))}
                </div>

                {filtered.map((item, idx) => (
                  <div key={item.id} style={{
                    display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto',
                    padding: '12px 16px', alignItems: 'center',
                    borderBottom: idx < filtered.length - 1 ? '1px solid #e2e8f0' : 'none',
                    background: item.is_low ? '#fffbeb' : '#fff',
                    transition: 'background 0.1s',
                    minWidth: 560,
                  }}
                    onMouseEnter={e => { if (!item.is_low) (e.currentTarget as HTMLElement).style.background = '#f8fafc'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = item.is_low ? '#fffbeb' : '#fff'; }}
                  >
                    {/* Name */}
                    <div>
                      <div style={{ fontWeight: 800, fontSize: 13, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {item.is_low && <AlertTriangle size={13} color="#d97706" />}
                        {item.name}
                      </div>
                      <div style={{ fontSize: 11, color: '#475569', fontWeight: 600, marginTop: 1, fontFamily: 'monospace' }}>{item.barcode}</div>
                    </div>

                    {/* Category */}
                    <div style={{ fontSize: 12, color: '#1e293b' }}>
                      {item.category ? (
                        <span style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#1e293b', borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 700 }}>{item.category}</span>
                      ) : <span style={{ color: '#94a3b8' }}>—</span>}
                    </div>

                    {/* Stock */}
                    <div>
                      <span style={{
                        fontSize: 16, fontWeight: 900,
                        color: item.stock_quantity === 0 ? '#dc2626' : item.is_low ? '#b45309' : '#15803d',
                      }}>
                        {item.stock_quantity}
                      </span>
                      {item.unit && <span style={{ fontSize: 11, color: '#475569', fontWeight: 600, marginLeft: 4 }}>{item.unit}</span>}
                    </div>

                    {/* Threshold (inline edit) */}
                    <div>
                      {editingThreshold === item.id ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <input
                            type="number"
                            value={thresholdVal}
                            onChange={e => setThresholdVal(e.target.value)}
                            style={{ ...inp, width: 56, padding: '4px 7px', fontSize: 12, fontWeight: 700 }}
                            autoFocus
                            onKeyDown={e => { if (e.key === 'Enter') saveThreshold(item); if (e.key === 'Escape') setEditingThreshold(null); }}
                          />
                          <button onClick={() => saveThreshold(item)} style={{ background: '#10b981', border: 'none', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', color: '#fff' }}>
                            <Check size={11} />
                          </button>
                          <button onClick={() => setEditingThreshold(null)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', color: '#334155' }}>
                            <X size={11} />
                          </button>
                        </div>
                      ) : (
                        <button onClick={() => { if (perms.canEditProduct) { setEditingThreshold(item.id); setThresholdVal(String(item.low_stock_threshold)); } }}
                          style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: perms.canEditProduct ? 'pointer' : 'default', color: '#0f172a', fontWeight: 800, fontSize: 13, padding: 0 }}>
                          {item.low_stock_threshold}
                          <Edit2 size={11} style={{ opacity: 0.6, color: '#475569' }} />
                        </button>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 5 }}>
                      {perms.canEditProduct && (
                        <button onClick={() => setAdjustItem(item)} title="Adjust stock"
                          style={{ padding: '5px 9px', borderRadius: 7, border: '1.5px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 700, color: '#374151', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <BarChart2 size={12} /> Adjust
                        </button>
                      )}
                      <button onClick={() => setLedgerItem(item)} title="View ledger"
                        style={{ padding: '5px 9px', borderRadius: 7, border: '1.5px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 11, color: '#6366f1', display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Clock size={12} /> History
                      </button>
                    </div>
                  </div>
                ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── PURCHASE ORDERS TAB ── */}
        {tab === 'orders' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              {perms.canAddProduct && (
  <button onClick={() => setShowPOModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: 'none', background: 'linear-gradient(to right,#10b981,#059669)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
    <Plus size={13} /> New Order
  </button>
)}
{perms.canImportProducts && (
  <label style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, background: '#f0fdf4', color: '#059669', cursor: 'pointer', fontSize: 12, fontWeight: 700, border: '1.5px solid #86efac' }}>
    📥 Import Excel
    <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={importPOExcel} />
  </label>
)}
{perms.canExportExcel && (
<button onClick={exportPOExcel} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontSize: 11, fontWeight: 700, color: '#374151' }}>
  📤 Export Excel
</button>
)}
            </div>

            {purchaseOrders.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', textAlign: 'center', width: '100%' }}>
                <Truck size={42} style={{ color: '#475569', marginBottom: 10 }} />
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>No purchase orders yet</div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>Create one to restock your inventory</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {purchaseOrders.map(po => {
                  const supplier = suppliers.find(s => s.id === po.supplier_id);
                  return (
                    <div key={po.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14 }}>
                      <div style={{
                        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                        background: po.status === 'received' ? '#f0fdf4' : '#fffbeb',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {po.status === 'received' ? <Check size={18} color="#10b981" /> : <Clock size={18} color="#f59e0b" />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>
                          PO #{po.id} {supplier ? `— ${supplier.name}` : ''}
                        </div>
                        <div style={{ fontSize: 12, color: '#1e293b', fontWeight: 600, marginTop: 3 }}>
                          {new Date(po.created_at).toLocaleDateString()} · <span style={{ color: '#0f172a', fontWeight: 900 }}>{currSym}{po.total_cost.toFixed(2)}</span>
                          {po.note && <span style={{ color: '#334155', fontWeight: 500 }}> · {po.note}</span>}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <span style={{
                          fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20,
                          background: po.status === 'received' ? '#f0fdf4' : '#fffbeb',
                          color: po.status === 'received' ? '#10b981' : '#f59e0b',
                          border: `1px solid ${po.status === 'received' ? '#86efac' : '#fde68a'}`,
                          textTransform: 'uppercase',
                        }}>
                          {po.status}
                        </span>
                        {po.status === 'pending' && perms.canEditProduct && (
                          <button
                            onClick={() => {
                              if (confirm(`Mark PO #${po.id} as Received?\n\nThis will update stock. ⚠️ This action is permanent — once marked as Received, the status cannot be changed back or edited.`)) {
                                receiveOrder(po.id);
                              }
                            }}
                            style={{ padding: '6px 12px', borderRadius: 8, border: 'none', background: 'linear-gradient(to right,#3b82f6,#6366f1)', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                          >
                            ✓ Mark Received
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── SUPPLIERS TAB ── */}
        {tab === 'suppliers' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
              {perms.canAddProduct && (
                <button onClick={() => setShowSupplierForm(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: 'none', background: 'linear-gradient(to right,#6366f1,#8b5cf6)', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                  <Plus size={13} />
                  Add Supplier
                </button>
              )}
              {perms.canImportProducts && (
  <label style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, background: '#eff6ff', color: '#3b82f6', cursor: 'pointer', fontSize: 12, fontWeight: 700, border: '1.5px solid #93c5fd' }}>
    📥 Import Excel
    <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={importSuppliersExcel} />
  </label>
)}
{perms.canExportExcel && (
<button onClick={exportSuppliersExcel} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px', borderRadius: 8, background: '#f0fdf4', color: '#059669', cursor: 'pointer', fontSize: 12, fontWeight: 700, border: '1.5px solid #86efac' }}>
  📤 Export Excel
</button>
)}
            </div>

            {showSupplierForm && (
  <Modal title="Add Supplier" onClose={() => setShowSupplierForm(false)}>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 800, color: '#1e293b', textTransform: 'uppercase', marginBottom: 4 }}>Name *</div>
        <input style={{ ...inp, width: '100%', color: '#0f172a', fontWeight: 600 }} placeholder="Supplier name" value={supName} onChange={e => setSupName(e.target.value)} />
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 800, color: '#1e293b', textTransform: 'uppercase', marginBottom: 4 }}>Phone</div>
        <PhoneInput
          value={supPhone}
          onChange={setSupPhone}
          orgDefault={(authUser as any)?.default_phone_country}
          outletDefault={outletPhoneDefault}
        />
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 800, color: '#1e293b', textTransform: 'uppercase', marginBottom: 4 }}>Email</div>
        <input style={{ ...inp, width: '100%', color: '#0f172a', fontWeight: 600 }} placeholder="email@..." value={supEmail} onChange={e => setSupEmail(e.target.value)} />
      </div>
      <div>
        <div style={{ fontSize: 11, fontWeight: 800, color: '#1e293b', textTransform: 'uppercase', marginBottom: 4 }}>Address</div>
        <input style={{ ...inp, width: '100%', color: '#0f172a', fontWeight: 600 }} placeholder="City, State" value={supAddress} onChange={e => setSupAddress(e.target.value)} />
      </div>
      <button onClick={createSupplier} disabled={supSaving} style={{
        width: '100%', padding: 11, borderRadius: 10, border: 'none',
        background: 'linear-gradient(to right,#6366f1,#8b5cf6)',
        color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer',
        opacity: supSaving ? 0.7 : 1,
      }}>
        {supSaving ? 'Saving...' : 'Save Supplier'}
      </button>
    </div>
  </Modal>
)}

            {suppliers.length === 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 0', textAlign: 'center', width: '100%' }}>
                <TrendingUp size={42} style={{ color: '#475569', marginBottom: 10 }} />
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>No suppliers added</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 10 }}>
                {suppliers.map(s => (
                  <div key={s.id} style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: '14px 16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>{s.name}</div>
                      {perms.canRemoveProduct && (
                        <button onClick={() => deleteSupplier(s.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db' }}>
                          <X size={13} />
                        </button>
                      )}
                    </div>
                    {s.phone && (
  <div style={{ fontSize: 12, color: '#1e293b', fontWeight: 600, marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
    <span>📞 {s.phone}</span>
    <a
      href={`https://wa.me/${s.phone.replace(/\D/g, '')}`}
      target="_blank"
      rel="noopener noreferrer"
      title="Chat on WhatsApp"
      style={{ color: '#15803d', textDecoration: 'none', fontSize: 11, fontWeight: 800, background: '#dcfce7', padding: '2px 8px', borderRadius: 6, border: '1px solid #bbf7d0' }}
    >
      💬 WA
    </a>
  </div>
)}
                    {s.email && <div style={{ fontSize: 12, color: '#334155', fontWeight: 500, marginTop: 3 }}>✉ {s.email}</div>}
                    {s.address && <div style={{ fontSize: 12, color: '#334155', fontWeight: 500, marginTop: 3 }}>📍 {s.address}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
{/* ── REPORTS TAB ── */}
        {tab === 'reports' && <InventoryReportsTab refreshKey={reportRefreshKey} userRole={authUser?.role} authUserPerms={authUser?.permissions} plan={(authUser as any)?.plan} planTier={(authUser as any)?.plan_tier} planStatus={(authUser as any)?.plan_status} moduleStatuses={moduleStatuses} outletId={outletId} />}
      {/* ── Modals ── */}
      {adjustItem && <AdjustModal item={adjustItem} outletId={outletId} onClose={() => setAdjustItem(null)} onDone={fetchAll} />}
      {ledgerItem && <LedgerDrawer item={ledgerItem} outletId={outletId} onClose={() => setLedgerItem(null)} />}
      {showPOModal && <PurchaseOrderModal suppliers={suppliers} inventory={inventory} outletId={outletId} onClose={() => setShowPOModal(false)} onDone={fetchAll} />}

      <style>{`@keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </div>
  );
}
